import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EmptyState, ErrorBanner, Spinner, StatusBadge, Toast } from '../components/ui';
import { api, type PublishedItem, type PublishedStatus } from '../lib/api';

const STATUSES: PublishedStatus[] = ['active', 'limited', 'pending', 'archived', 'deleted', 'failed'];
const PAGE_SIZE = 50;

/**
 * Dashboard of everything published across all accounts.
 *
 * Two operations are inline because they are the ones done dozens of times a
 * day: editing a price and archiving. Both mutate through the backend (which
 * rebuilds the full OLX payload) and then invalidate the list.
 */
export function AdvertListPage() {
  const [accountId, setAccountId] = useState<number | undefined>();
  const [status, setStatus] = useState<PublishedStatus | undefined>('active');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: api.accounts.list });

  const adverts = useQuery({
    queryKey: ['adverts', { accountId, status, search, page }],
    queryFn: () =>
      api.adverts.list({
        ...(accountId !== undefined ? { accountId } : {}),
        ...(status !== undefined ? { status } : {}),
        ...(search ? { search } : {}),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });

  const sync = useMutation({
    mutationFn: () => api.adverts.sync(accountId),
    onSuccess: async () => {
      setNotice('Sync finished — statuses, prices and limit usage refreshed from OLX.');
      await queryClient.invalidateQueries({ queryKey: ['adverts'] });
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['adverts'] });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Adverts</h1>
          <p className="mt-1 text-sm text-slate-400">
            {adverts.data ? `${adverts.data.total} tracked` : 'Loading…'} across all managed accounts.
          </p>
        </div>
        <button type="button" className="btn-ghost" onClick={() => sync.mutate()} disabled={sync.isPending}>
          {sync.isPending ? <Spinner /> : null}
          Sync from OLX
        </button>
      </div>

      {notice ? <Toast tone="success">{notice}</Toast> : null}

      <div className="card flex flex-wrap items-end gap-4 p-4">
        <div className="min-w-48">
          <label className="label" htmlFor="filter-account">
            Account
          </label>
          <select
            id="filter-account"
            className="input"
            value={accountId ?? ''}
            onChange={(event) => {
              setAccountId(event.target.value ? Number(event.target.value) : undefined);
              setPage(0);
            }}
          >
            <option value="">All accounts</option>
            {accounts.data?.map((account) => (
              <option key={account.id} value={account.id}>
                {account.label ?? account.name}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-40">
          <label className="label" htmlFor="filter-status">
            Status
          </label>
          <select
            id="filter-status"
            className="input"
            value={status ?? ''}
            onChange={(event) => {
              setStatus((event.target.value || undefined) as PublishedStatus | undefined);
              setPage(0);
            }}
          >
            <option value="">Any status</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-64 flex-1">
          <label className="label" htmlFor="filter-search">
            Search title or WP product id
          </label>
          <input
            id="filter-search"
            className="input"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            placeholder="Климатик, 4821…"
          />
        </div>
      </div>

      {adverts.isError ? <ErrorBanner error={adverts.error} onRetry={() => adverts.refetch()} /> : null}

      <div className="card overflow-hidden">
        {adverts.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="h-6 w-6 text-slate-500" />
          </div>
        ) : adverts.data && adverts.data.items.length > 0 ? (
          <table className="w-full">
            <thead className="border-b border-border-subtle bg-surface-3/50">
              <tr>
                <th className="th">Advert</th>
                <th className="th">Account</th>
                <th className="th">WP ID</th>
                <th className="th">Price</th>
                <th className="th">Status</th>
                <th className="th text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle/60">
              {adverts.data.items.map((item) => (
                <AdvertRow key={item.id} item={item} onChanged={invalidate} onNotice={setNotice} />
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            title="No adverts match these filters"
            hint="Publish one from the Add advert tab, or run Sync from OLX to import adverts created directly on olx.bg."
          />
        )}
      </div>

      {adverts.data && adverts.data.total > PAGE_SIZE ? (
        <div className="flex items-center justify-between text-sm text-slate-400">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, adverts.data.total)} of {adverts.data.total}
          </span>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <button
              type="button"
              className="btn-ghost"
              disabled={(page + 1) * PAGE_SIZE >= adverts.data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdvertRow({
  item,
  onChanged,
  onNotice,
}: {
  item: PublishedItem;
  onChanged: () => void;
  onNotice: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(item.priceValue ?? '');
  const [error, setError] = useState<unknown>(null);

  const updatePrice = useMutation({
    mutationFn: (value: number) => api.adverts.updatePrice(item.id, value),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      onNotice(`Price updated for "${item.title.slice(0, 40)}".`);
      onChanged();
    },
    onError: setError,
  });

  const archive = useMutation({
    mutationFn: (options: { isSuccess: boolean; hardDelete: boolean }) => api.adverts.archive(item.id, options),
    onSuccess: (updated) => {
      onNotice(`Advert ${updated.status === 'deleted' ? 'deleted' : 'archived'}.`);
      onChanged();
    },
    onError: setError,
  });

  const canModify = item.status === 'active' || item.status === 'limited' || item.status === 'pending';

  return (
    <tr className="hover:bg-surface-3/30">
      <td className="td max-w-md">
        <div className="truncate font-medium text-slate-100" title={item.title}>
          {item.olxUrl ? (
            <a href={item.olxUrl} target="_blank" rel="noreferrer" className="hover:text-accent hover:underline">
              {item.title}
            </a>
          ) : (
            item.title
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-slate-500" title={item.olxCategoryPath ?? undefined}>
          {item.olxCategoryPath ?? '—'}
        </div>
        {error ? (
          <div className="mt-2">
            <ErrorBanner error={error} />
          </div>
        ) : null}
      </td>

      <td className="td whitespace-nowrap text-slate-300">{item.account.label ?? item.account.name}</td>

      <td className="td whitespace-nowrap">
        <code className="rounded bg-surface px-1.5 py-0.5 text-xs text-slate-400">{item.wpProductId}</code>
      </td>

      <td className="td whitespace-nowrap">
        {editing ? (
          <form
            className="flex items-center gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              updatePrice.mutate(Number(price));
            }}
          >
            <input
              className="input w-24 py-1"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              type="number"
              step="0.01"
              min="0"
              autoFocus
              aria-label="New price"
            />
            <button type="submit" className="btn-primary px-2 py-1" disabled={updatePrice.isPending}>
              {updatePrice.isPending ? <Spinner /> : 'Save'}
            </button>
            <button
              type="button"
              className="btn-ghost px-2 py-1"
              onClick={() => {
                setEditing(false);
                setPrice(item.priceValue ?? '');
                setError(null);
              }}
            >
              ✕
            </button>
          </form>
        ) : (
          <button
            type="button"
            className="rounded px-1.5 py-0.5 text-left tabular-nums hover:bg-surface-3 disabled:cursor-default disabled:hover:bg-transparent"
            onClick={() => setEditing(true)}
            disabled={!canModify}
            title={canModify ? 'Click to edit the price' : 'Only live adverts can be repriced'}
          >
            {item.priceValue ? `${Number(item.priceValue).toFixed(2)} ${item.priceCurrency ?? ''}` : '—'}
          </button>
        )}
      </td>

      <td className="td">
        <StatusBadge status={item.status} olxStatus={item.olxStatus} />
      </td>

      <td className="td text-right whitespace-nowrap">
        {canModify ? (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-ghost px-2 py-1 text-xs"
              disabled={archive.isPending}
              onClick={() => archive.mutate({ isSuccess: true, hardDelete: false })}
              title="Deactivate on OLX and mark as sold"
            >
              Sold
            </button>
            <button
              type="button"
              className="btn-danger px-2 py-1 text-xs"
              disabled={archive.isPending}
              onClick={() => archive.mutate({ isSuccess: false, hardDelete: true })}
              title="Deactivate, then permanently delete from OLX"
            >
              {archive.isPending ? <Spinner /> : 'Archive'}
            </button>
          </div>
        ) : (
          <span className="text-xs text-slate-600">—</span>
        )}
      </td>
    </tr>
  );
}
